export type RabbitAnimationPhase = "crouch" | "launch" | "airborne" | "land" | "pause" | "startled";

export interface RabbitVisualState {
  phase: RabbitAnimationPhase;
  lift: number;
  forwardOffset: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  shadowScale: number;
  shadowAlpha: number;
}

const HOP_SECONDS = 1.08;
const MAX_LIFT = 7.5;

function stableUnitInterval(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0x1_0000_0000;
}

function smoothstep(value: number): number {
  const bounded = Math.max(0, Math.min(1, value));
  return bounded * bounded * (3 - 2 * bounded);
}

function reactionState(age: number, reducedMotion: boolean): RabbitVisualState | null {
  if (age < 0 || age >= 0.58) return null;
  const progress = age / 0.58;
  const bounce = Math.sin(Math.PI * progress);
  return {
    phase: "startled",
    lift: reducedMotion ? bounce * 1.2 : bounce * 10,
    forwardOffset: reducedMotion ? 0 : Math.sin(progress * Math.PI * 2) * 1.2,
    scaleX: reducedMotion ? 1 : 1 + 0.12 * bounce,
    scaleY: reducedMotion ? 1 : 1 - 0.1 * bounce,
    rotation: reducedMotion ? 0 : Math.sin(progress * Math.PI * 2) * 0.24,
    shadowScale: reducedMotion ? 0.94 : 1 - 0.34 * bounce,
    shadowAlpha: reducedMotion ? 0.27 : 0.3 - 0.14 * bounce,
  };
}

export function rabbitVisualState(
  rabbitId: string,
  raceSeed: number,
  simulationSeconds: number,
  reducedMotion = false,
  incidentAgeSeconds: number | null = null,
): RabbitVisualState {
  if (incidentAgeSeconds !== null) {
    const reaction = reactionState(incidentAgeSeconds, reducedMotion);
    if (reaction) return reaction;
  }

  const offset = stableUnitInterval(`${raceSeed}:${rabbitId}`);
  const cycle = ((simulationSeconds / HOP_SECONDS + offset) % 1 + 1) % 1;
  if (reducedMotion) {
    const breath = 0.5 + 0.5 * Math.sin(cycle * Math.PI * 2);
    return {
      phase: cycle < 0.18 ? "crouch" : cycle < 0.82 ? "airborne" : "pause",
      lift: breath * 0.65,
      forwardOffset: 0,
      scaleX: 1 + 0.025 * breath,
      scaleY: 1 - 0.025 * breath,
      rotation: 0,
      shadowScale: 0.96,
      shadowAlpha: 0.28,
    };
  }

  if (cycle < 0.14) {
    const progress = smoothstep(cycle / 0.14);
    return {
      phase: "crouch",
      lift: 0,
      forwardOffset: -1.1 * progress,
      scaleX: 1 + 0.12 * progress,
      scaleY: 1 - 0.2 * progress,
      rotation: 0.035 * progress,
      shadowScale: 1.06,
      shadowAlpha: 0.32,
    };
  }
  if (cycle < 0.28) {
    const progress = smoothstep((cycle - 0.14) / 0.14);
    return {
      phase: "launch",
      lift: MAX_LIFT * 0.72 * progress,
      forwardOffset: -1.1 + 2.3 * progress,
      scaleX: 1.12 - 0.02 * progress,
      scaleY: 0.8 + 0.35 * progress,
      rotation: 0.035 - 0.07 * progress,
      shadowScale: 1.06 - 0.25 * progress,
      shadowAlpha: 0.32 - 0.1 * progress,
    };
  }
  if (cycle < 0.76) {
    const progress = (cycle - 0.28) / 0.48;
    const arc = Math.sin(Math.PI * progress);
    return {
      phase: "airborne",
      lift: MAX_LIFT * (0.72 + 0.28 * arc),
      forwardOffset: 1.2 + 0.55 * Math.sin(Math.PI * progress),
      scaleX: 1.1,
      scaleY: 1.15,
      rotation: -0.035 + 0.07 * progress,
      shadowScale: 0.81 - 0.13 * arc,
      shadowAlpha: 0.22 - 0.07 * arc,
    };
  }
  if (cycle < 0.9) {
    const progress = smoothstep((cycle - 0.76) / 0.14);
    return {
      phase: "land",
      lift: MAX_LIFT * 0.72 * (1 - progress),
      forwardOffset: 1.2 * (1 - progress),
      scaleX: 1.1 + 0.16 * progress,
      scaleY: 1.15 - 0.33 * progress,
      rotation: 0.035 * (1 - progress),
      shadowScale: 0.81 + 0.27 * progress,
      shadowAlpha: 0.22 + 0.1 * progress,
    };
  }
  const progress = smoothstep((cycle - 0.9) / 0.1);
  return {
    phase: "pause",
    lift: 0,
    forwardOffset: 0,
    scaleX: 1.26 - 0.26 * progress,
    scaleY: 0.82 + 0.18 * progress,
    rotation: 0,
    shadowScale: 1.08 - 0.08 * progress,
    shadowAlpha: 0.32 - 0.02 * progress,
  };
}
